import React from "react";
import { useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";

import Dialog from "src/components/Dialog";
import LoadableButton from "src/components/LoadableButton";
import { setActiveDialog } from "src/store/adminHtml/actions";
import { addGeneralLedgerAccount } from "src/apiService/modules/generalLedgerAccounts";
import type {
  GeneralLedgerAccountV2,
  GeneralLedgerAccountV2Type,
} from "src/interfaces/generalLedgerAccount.interface";
import { GeneralLedgerAccountTypeToLabelMaps } from "src/utils/general-ledger-accounts";
import { generalLedgerAccountsSelector } from "src/store/glAccounts/selector";

function normalizeAccountName(name: string) {
  return name.toLowerCase().trim().replace(/\s+/g, "");
}

function Content({
  onClose,
}: {
  onClose: (account?: GeneralLedgerAccountV2) => void;
}) {
  const allGlAccounts = useSelector(generalLedgerAccountsSelector);

  const formik = useFormik({
    initialValues: {
      name: "",
      type: "",
    },
    validateOnMount: true,
    validate: (values) => {
      const errors: { name?: string; type?: string } = {};
      const normalizedName = normalizeAccountName(values.name || "");

      if (!values.type) {
        errors.type = "Please select an account type.";
      }
      if (!normalizedName) {
        errors.name = "Please enter an account name.";
      } else {
        const duplicateExists = allGlAccounts.some((account) => {
          if (account.deleted) return false;
          return normalizeAccountName(account.name) === normalizedName;
        });
        if (duplicateExists) {
          errors.name =
            "You already have an account with this name. Please choose a different name.";
        }
      }

      return errors;
    },
    onSubmit: async (values) => {
      try {
        const type = values.type.split("/") as GeneralLedgerAccountV2Type;
        const account = await addGeneralLedgerAccount(values.name, type);
        onClose(account);
        toast.success(`General Ledger Account created successfully.`);
      } catch (e) {
        const message = (e as Error).message || (e as Error).toString();
        if (
          message.includes("already exists") ||
          message.includes("cannot be empty")
        ) {
          formik.setFieldError(
            "name",
            "You already have an account with this name. Please choose a different name."
          );
          return;
        }
        toast.error(`There was an error: ${message}`);
      }
    },
  });

  return (
    <form onSubmit={formik.handleSubmit}>
      <DialogTitle>Add General Ledger Account</DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField
              select
              fullWidth
              value={formik.values.type}
              onChange={formik.handleChange}
              label="Account Type"
              disabled={formik.isSubmitting}
              name="type"
              required
              error={formik.touched.type && !!formik.errors.type}
              helperText={formik.touched.type ? formik.errors.type : undefined}
              onBlur={formik.handleBlur}
              SelectProps={{ native: true }}
            >
              <option aria-label="None" value="" disabled />
              {Object.entries(GeneralLedgerAccountTypeToLabelMaps).map(
                ([key, value]) => {
                  const types = Object.entries(value.types);
                  types.sort((a, b) => a[1].localeCompare(b[1]));
                  return (
                    <optgroup key={key} label={value.label}>
                      {types.map(([k, v]) => (
                        <option key={`${key}/${k}`} value={`${key}/${k}`}>
                          {v}
                        </option>
                      ))}
                    </optgroup>
                  );
                }
              )}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Account Name"
              variant="standard"
              name="name"
              value={formik.values.name}
              required
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.name && !!formik.errors.name}
              helperText={formik.touched.name ? formik.errors.name : undefined}
              disabled={formik.isSubmitting}
              autoComplete="off"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <LoadableButton
          color="primary"
          variant="contained"
          loading={formik.isSubmitting}
          disabled={formik.isSubmitting || !formik.isValid}
          type="submit"
        >
          Add
        </LoadableButton>
      </DialogActions>
    </form>
  );
}

export function AddGeneralLedgerAccountDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: (account?: GeneralLedgerAccountV2) => void;
}) {
  return (
    <Dialog
      open={open}
      onClose={() => onClose()}
      scroll="body"
      fullWidth={true}
      maxWidth="sm"
    >
      <Content onClose={onClose} />
    </Dialog>
  );
}

function AddGeneralLedgerAccountRootDialog({ open }: { open: boolean }) {
  const dispatch = useDispatch();
  const handleClose = () => {
    dispatch(setActiveDialog(""));
  };

  return <AddGeneralLedgerAccountDialog open={open} onClose={handleClose} />;
}

export default AddGeneralLedgerAccountRootDialog;
